import bind from 'memoize-bind';
import React, { Component, createRef } from 'react';

import { SelectionContext } from './SelectionContext';
import { DATA_ATTRIBUTE_INDEX, SelectionItem } from './SelectionItem';

export interface SelectionChange<T = any> {
  added: T[];
  removed: T[];
  selections: T[];
}

interface SelectionProps<T = any> {
  disabled: boolean;
  mode: 'single' | 'multiple';
  requireModifier: boolean;
  max?: number;
  min?: number;
  selections: T[];
  onSelectionChange: (event: SelectionChange<T>) => void;
}

export class Selection<T = any> extends Component<
  React.PropsWithChildren<SelectionProps<T>>,
  {}
> {
  static defaultProps = {
    disabled: false,
    mode: 'multiple',
    selections: [],
    requireModifier: false,
    onSelectionChange: () => undefined,
  };

  elementRef = createRef<HTMLDivElement>();
  items: SelectionItem<T>[] = [];
  previousSelectedIndex = -1;

  onItemToggle(item: SelectionItem<T>) {
    if (this.props.disabled) {
      return;
    }

    if (Array.isArray(item.props.model)) {
      this.setArraySelection(item.props.model);
    } else {
      this.setSelection(item);
    }
  }

  onRegister(model: SelectionItem<T>) {
    this.items.push(model);
  }

  onUnregister(model: SelectionItem<T>) {
    const index = this.items.indexOf(model);
    if (index > -1) {
      this.items.splice(index, 1);
    }
  }

  hasMaxSelections(model: T[]) {
    const { max, selections } = this.props;
    return !max || model.length + selections.length < max;
  }

  setArraySelection(models: T[]) {
    const { mode } = this.props;
    if (mode === 'multiple' && this.hasMaxSelections(models)) {
      this.onSelectionChange(models, []);
    }
  }

  setSelection(item: SelectionItem<T>) {
    const sortedItems = this.getSortedItems();
    const selectedIndex = this.getToggleIndex(sortedItems, item);

    if (this.props.mode === 'multiple') {
      this.setMultipleSelections(sortedItems, selectedIndex, item);
    } else {
      this.setSingleSelections(item.props.model as T);
    }

    this.previousSelectedIndex = selectedIndex;
  }

  setSingleSelections(model: T) {
    const { selections } = this.props;

    const isSelected = selections.find((s) => s === model);
    if (!isSelected) {
      this.onSelectionChange([model], [], true);
    } else {
      this.onSelectionChange([], [model], true);
    }
  }

  setMultipleSelections(
    sortedItems: SelectionItem<T>[],
    selectedIndex: number,
    item: SelectionItem<T>
  ) {
    const { selections, requireModifier, max } = this.props;
    const modifier = item.modifier;
    const model = item.props.model as T;

    if (modifier === 'shift') {
      if (selectedIndex > -1) {
        this.selectRange(
          sortedItems,
          this.previousSelectedIndex,
          selectedIndex
        );
      }
    } else {
      const isSelected = selections.find((s) => s === model);
      if (requireModifier && modifier !== 'meta') {
        const additions: T[] = [];

        if (selections.length > 1) {
          additions.push(model);
        } else if (!isSelected) {
          additions.push(model);
        }

        const removals: T[] = selections.filter((s) => !additions.includes(s));

        this.onSelectionChange(additions, removals);
      } else {
        if (!isSelected) {
          if (!max || selections.length < max) {
            this.onSelectionChange([model], []);
          }
        } else {
          this.onSelectionChange([], [model]);
        }
      }
    }
  }

  selectRange(
    sortedItems: SelectionItem<T>[],
    sourceIndex: number,
    targetIndex: number
  ) {
    const { selections, max } = this.props;

    // On init we don't have a previous index, find it based on the first selection
    if (sourceIndex === null && selections.length) {
      const [selection] = selections;
      sourceIndex = sortedItems.findIndex(
        (item) => item.props.model === selection
      );
    }

    const reverse = targetIndex < sourceIndex;
    const start = Math.max(reverse ? targetIndex : sourceIndex, 0);
    const end = Math.min(
      reverse ? sourceIndex : targetIndex,
      sortedItems.length - 1
    );
    const result: T[] = [];

    for (let i = start; i <= end; i++) {
      const item = sortedItems[i];
      if (!item.props.disabled) {
        result.push(item.props.model as T);
      }
    }

    if (!max || result.length + selections.length < max) {
      this.onSelectionChange(result, []);
    }
  }

  onSelectionChange(additions: T[], removals: T[], removeAll = false) {
    const current = [...this.props.selections];
    const added = additions.filter((a) => !current.includes(a));

    if (removeAll) {
      removals = current;
    }

    const removed = removals.filter((r) => current.includes(r));
    const filteredCurrent = current.filter(
      (c) => !removed.includes(c) && !added.includes(c)
    );
    const selections = [...filteredCurrent, ...added];

    this.props.onSelectionChange({
      added,
      removed,
      selections,
    });
  }

  getSortedItems(): SelectionItem<T>[] {
    const elements = this.getElements();
    return this.items.sort(
      (a, b) =>
        elements.indexOf(a.elementRef.current!) -
        elements.indexOf(b.elementRef.current!)
    );
  }

  getElements(): HTMLDivElement[] {
    if (this.elementRef.current) {
      return [].slice.call(
        this.elementRef.current.querySelectorAll(
          `[${DATA_ATTRIBUTE_INDEX}="true"]`
        )
      );
    }
    return [];
  }

  getToggleIndex(
    sortedItems: SelectionItem<T>[],
    sourceItem: SelectionItem<T>
  ) {
    let index = 0;
    let foundIndex = -1;

    for (const item of sortedItems) {
      if (item.elementRef.current === sourceItem.elementRef.current) {
        foundIndex = index;
        break;
      }
      index++;
    }

    return foundIndex;
  }

  render() {
    const { children, selections } = this.props;

    return (
      <div ref={this.elementRef}>
        <SelectionContext.Provider
          value={{
            toggle: bind(this.onItemToggle, this),
            register: bind(this.onRegister, this),
            unregister: bind(this.onUnregister, this),
            selections,
          }}
        >
          {children}
        </SelectionContext.Provider>
      </div>
    );
  }
}
